import { useMemo } from 'react';
import { INCOME_GROUP_LABELS, type IncomeReport, type IncomeRow } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { Segmented } from '#client/components/ui/Segmented';
import { useApiResource } from '#client/hooks/useResourceItem';
import { useUrlState } from '#client/hooks/useUrlState';
import { formatCount, formatMoney } from '#client/lib/format';
import { ENDPOINT, withQuery } from '#client/lib/endpoints';
import { DateRangeBar } from './DateRangeBar';
import { INCOME_GROUP_OPTIONS, parseIncomeGroup } from './reports.logic';
import { useDateRange } from './useDateRange';

/**
 * Revenue for a period, broken down five ways.
 *
 * The screen's one piece of honesty: **the collected and outstanding columns
 * disappear** when the grouping is per-test or per-bench. Payment is recorded
 * against a visit, so it cannot be attributed to a line item, and printing a
 * zero there would read as "nothing was paid" rather than "this is not
 * knowable". The server says which it is via `collected_known`.
 */
export function IncomePage() {
  const range = useDateRange();
  const { params, setParam } = useUrlState();
  const by = parseIncomeGroup(params.get('by'));

  const report = useApiResource<IncomeReport>(
    withQuery(ENDPOINT.income, { from: range.from, to: range.to, by }),
  );

  const columns = useMemo(
    () => buildColumns(report.data?.count_label ?? '', report.data?.collected_known ?? true),
    [report.data?.count_label, report.data?.collected_known],
  );

  if (report.loading) return <PageLoading title="Income" fullWidth />;
  if (report.error || !report.data) {
    return (
      <PageMessage title="Income" fullWidth>
        {report.error ?? 'Could not load the income breakdown.'}
      </PageMessage>
    );
  }

  const { rows, totals, collected_known } = report.data;

  return (
    <Page
      title="Income"
      subtitle={`${formatCount(totals.count)} ${report.data.count_label.toLowerCase()} · ${formatMoney(totals.billed)} billed`}
      fullWidth
    >
      <div className="c-form-stack">
        <Card>
          <DateRangeBar range={range} />
          <div className="c-range-bar__group">
            <Segmented
              label="Group income by"
              options={INCOME_GROUP_OPTIONS}
              value={by}
              onChange={(next) => setParam('by', next)}
              size="sm"
            />
          </div>
        </Card>

        {rows.length === 0 ? (
          <EmptyState
            title="Nothing registered in this period"
            body="Widen the dates, or check that the visits you expect were registered on the days you think."
          />
        ) : (
          <Card
            title={INCOME_GROUP_LABELS[by]}
            subtitle={
              collected_known
                ? undefined
                : // Said on the card rather than in a tooltip: it changes how the
                  // numbers should be read, and a reader who misses it draws the
                  // wrong conclusion silently.
                  'Billed at list price. Payment is recorded against a visit, not a test, so it cannot be split across these rows.'
            }
            flush
          >
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => row.key}
              caption={`Income ${INCOME_GROUP_LABELS[by].toLowerCase()}`}
            />
          </Card>
        )}

        <Card title="Period total">
          <dl className="c-summary">
            <Total label={report.data.count_label} value={formatCount(totals.count)} />
            <Total label="Billed" value={formatMoney(totals.billed)} />
            {collected_known && (
              <>
                <Total label="Collected" value={formatMoney(totals.collected)} />
                <Total label="Outstanding" value={formatMoney(totals.outstanding)} />
              </>
            )}
          </dl>
        </Card>
      </div>
    </Page>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="c-summary__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function buildColumns(countLabel: string, collectedKnown: boolean): Column<IncomeRow>[] {
  return [
    { key: 'label', header: 'Name', render: (row) => row.label, frozen: true },
    {
      key: 'count',
      header: countLabel,
      width: '130px',
      align: 'end',
      render: (row) => <span className="tnum">{formatCount(row.count)}</span>,
    },
    {
      key: 'billed',
      header: 'Billed',
      width: '150px',
      align: 'end',
      render: (row) => <span className="tnum">{formatMoney(row.billed)}</span>,
    },
    ...(collectedKnown
      ? [
          {
            key: 'collected',
            header: 'Collected',
            width: '150px',
            align: 'end' as const,
            render: (row: IncomeRow) => <span className="tnum">{formatMoney(row.collected)}</span>,
          },
          {
            key: 'outstanding',
            header: 'Outstanding',
            width: '150px',
            align: 'end' as const,
            render: (row: IncomeRow) => (
              <span className={row.outstanding > 0 ? 'tnum c-money--due' : 'tnum'}>
                {formatMoney(row.outstanding)}
              </span>
            ),
          },
        ]
      : []),
  ];
}
